import { ActionIcon, Box, Flex, parseThemeColor, Text, useMantineTheme } from '@mantine/core';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { memo, useState } from 'react';
import {
  EntityCategory,
  selectIsDiscarded,
  selectIsResolved,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import { FieldTypeMetadata, FieldValue } from '../types';
import DataInputField from './form/DataInputField';

type DataInputProps = {
  children: React.ReactNode;
  highlight?: 'new' | 'existing';
  value?: FieldValue;
  onChange?: (value: FieldValue) => void;
  fieldName?: string;
  detectionId?: string;
  disabled?: boolean;
  allowDiscard?: boolean;
  editIconColor?: string;
  category?: EntityCategory;
  ariaLabelledBy?: string;
} & (
  | {
      editable: true;
      metadata: FieldTypeMetadata;
    }
  | {
      editable: false;
      metadata?: FieldTypeMetadata;
    }
);

export function DataInput({
  children,
  highlight,
  editable = false,
  metadata,
  value,
  onChange,
  detectionId,
  fieldName,
  allowDiscard = false,
  editIconColor,
  category,
  ariaLabelledBy,
}: DataInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const isBlank = (typeof children === 'string' && children.length === 0) || children == null;
  const saveLocalFieldValue = useDetectedEntitiesStore(state => state.saveLocalFieldValue);
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detectionId, fieldName),
  );
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detectionId, fieldName),
  );
  const isResolvedOrDiscarded = isResolved || isDiscarded;

  if (!editable) {
    return (
      <Box
        component="span"
        px="xxxs"
        display="inline-block"
        style={{
          borderRadius: 4,
        }}
        bg={isBlank ? undefined : backgroundColorForHighlight(highlight)}
        opacity={isResolvedOrDiscarded ? 0.5 : 1}
      >
        <ReadableValue metadata={metadata}>{children}</ReadableValue>
      </Box>
    );
  }

  if (!isEditing || metadata == null || isResolvedOrDiscarded) {
    return (
      <Flex
        gap="xxxs"
        align="center"
        style={{
          display: 'inline-flex',
        }}
      >
        <Box
          px="xxxs"
          style={{
            borderRadius: 4,
            cursor: 'pointer',
          }}
          bg={isBlank ? undefined : backgroundColorForHighlight(highlight)}
          onClick={() => {
            if (isResolvedOrDiscarded) {
              return;
            }
            setIsEditing(true);
          }}
          opacity={isResolvedOrDiscarded ? 0.5 : 1}
        >
          <ReadableValue metadata={metadata} ariaLabelledBy={ariaLabelledBy}>
            {children}
          </ReadableValue>
        </Box>
        <FieldActions
          allowDiscard={allowDiscard}
          detectionId={detectionId}
          fieldName={fieldName}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          editIconColor={editIconColor}
        />
      </Flex>
    );
  }

  return (
    <Flex
      gap="xxxs"
      align="center"
      style={{
        display: 'inline-flex',
      }}
    >
      <Box
        px="xxxs"
        style={{
          borderRadius: 4,
          display: 'inline-block',
        }}
        bg={backgroundColorForHighlight(highlight)}
      >
        <DataInputField
          metadata={metadata}
          value={
            value !== undefined
              ? value
              : typeof children === 'string' ||
                  typeof children === 'number' ||
                  typeof children === 'boolean'
                ? children
                : ''
          }
          onChange={(newValue: FieldValue) => {
            onChange?.(newValue);
            setIsEditing(false);
            if (category != null && detectionId != null && fieldName != null) {
              saveLocalFieldValue(category, detectionId, fieldName, newValue);
            }
          }}
          onCancel={() => setIsEditing(false)}
          highlight={highlight}
          fieldName={fieldName}
          ariaLabelledBy={ariaLabelledBy}
        />
      </Box>
      <FieldActions
        allowDiscard={allowDiscard}
        detectionId={detectionId}
        fieldName={fieldName}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        category={category}
      />
    </Flex>
  );
}

function backgroundColorForHighlight(highlight?: 'new' | 'existing') {
  if (highlight === 'new') {
    return 'blue.2';
  }

  if (highlight === 'existing') {
    return 'brand.1';
  }

  return undefined;
}

const FieldActions = memo(function FieldActions({
  allowDiscard,
  detectionId,
  fieldName,
  isEditing,
  setIsEditing,
  disabled,
  editIconColor,
  category,
}: {
  allowDiscard: boolean;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  detectionId?: string;
  fieldName?: string;
  disabled?: boolean;
  editIconColor?: string;
  category?: EntityCategory;
}) {
  const setDiscarded = useDetectedEntitiesStore(state => state.setDiscarded);
  const showDiscard = allowDiscard && detectionId != null && fieldName != null;
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detectionId, fieldName),
  );
  const showEdit = !showDiscard || !isDiscarded;
  const theme = useMantineTheme();
  const editIconColorRaw = editIconColor
    ? parseThemeColor({ color: editIconColor, theme: theme }).value
    : undefined;

  if (isEditing) {
    return null;
  }

  return (
    <Flex gap="xxxs" align="center">
      {showEdit && (
        <ActionIcon
          variant="transparent"
          onClick={() => setIsEditing(true)}
          c="black"
          bg="transparent"
          disabled={disabled}
          data-testid="input-edit-button"
          size="xs"
        >
          <IconPencil size={16} color={editIconColorRaw} />
        </ActionIcon>
      )}
      {showDiscard &&
        category != null &&
        (isDiscarded ? (
          <Flex gap="xxxs">
            <Text size="sm" c="gray.10" fw={600}>
              Discarded
            </Text>
            <Text
              size="sm"
              c="gray.7"
              fw={600}
              style={{ cursor: 'pointer' }}
              onClick={() =>
                setDiscarded(false, category, detectionId, { fieldNames: [fieldName] })
              }
              data-testid="input-undo-button"
            >
              Undo
            </Text>
          </Flex>
        ) : (
          <ActionIcon
            variant="subtle"
            c="error.6"
            onClick={() => setDiscarded(true, category, detectionId, { fieldNames: [fieldName] })}
            data-testid="input-discard-button"
            size="xs"
          >
            <IconTrash size={16} />
          </ActionIcon>
        ))}
    </Flex>
  );
});

function ReadableValue({
  children,
  metadata,
  ariaLabelledBy,
}: {
  children: React.ReactNode;
  metadata?: FieldTypeMetadata;
  ariaLabelledBy?: string;
}) {
  return (
    <Text size={'sm'} fw={500} c="gray.10" aria-labelledby={ariaLabelledBy}>
      {renderValue(children, metadata)}
    </Text>
  );
}

function renderValue(value: React.ReactNode, metadata?: FieldTypeMetadata) {
  if (value == null) {
    return '-';
  }

  if (metadata?.type === 'BOOLEAN') {
    return value ? 'Yes' : 'No';
  }

  if (metadata?.type === 'DATE') {
    if (typeof value === 'string') {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString('en-GB');
    }
  }

  if (metadata?.type === 'SELECT') {
    const option = metadata.options.find(o => {
      if (typeof value === 'string' && typeof o.value === 'string') {
        return o.value.toLowerCase() === value.toLowerCase();
      }

      return o.value === value;
    });
    if (option) {
      return option.label;
    }
  }

  if (metadata?.type === 'PERCENTAGE') {
    return `${value}%`;
  }

  if (metadata?.type === 'MONEY') {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: metadata.currency,
    }).format(Number(value));
  }

  if (typeof value === 'string' && value.length === 0) {
    return '-';
  }

  return value;
}
